import {
  ApolloClient,
  ApolloLink,
  from,
  InMemoryCache,
  split,
} from "@apollo/client";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { typePolicies } from "./apolloTypePolicies";
import { skipHiddenPagePoll } from "./skipHiddenPagePoll";
import { notifyWsConnected } from "./wsReconnect";

type DiffOptions = Parameters<InMemoryCache["diff"]>[0];

const stripCanonizeResults = (
  options?: DiffOptions
): DiffOptions | undefined => {
  if (!options || !("canonizeResults" in options)) return options;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { canonizeResults, ...rest } = options as unknown as Record<
    string,
    unknown
  > & {
    canonizeResults?: unknown;
  };
  return rest as unknown as DiffOptions;
};

export const createApolloClient = (scope: "admin" | "partner") => {
  const pathnameHttp = `${scope}/graphql`;
  const pathnameWs = pathnameHttp; // Используем тот же путь для WebSocket

  // HTTP GraphQL остаётся same-origin: каждый инстанс cabinet
  // (admin.ottkit.io / partner.ottkit.io) проксирует /*/graphql на бэкенд
  // через свой Firebase Hosting rewrite (см. firebase.json) — так же, как
  // Vite dev-прокси делает это в деве.
  const endpointHttp = `${window.location.origin}/${pathnameHttp}`;

  // WebSocket-подписки идут через отдельный поддомен, общий для всех
  // платформ и cabinet — Firebase Hosting rewrite на Cloud Run не тянет
  // WebSocket upgrade надёжно. VITE_WS_ORIGIN не задан в деве — используем
  // тот же origin, что и раньше (совпадает с Vite dev-прокси, у которого
  // ws: true).
  const wsOrigin =
    import.meta.env.VITE_WS_ORIGIN ||
    `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;
  const endpointWs = `${wsOrigin}/${pathnameWs}`;

  const uploadLink = createUploadLink({
    uri: endpointHttp,
  });

  if (import.meta.env.DEV) {
    // Adds messages only in a dev environment
    loadDevMessages();
    loadErrorMessages();
  }

  // WebSocket link для subscriptions
  const wsLink = new GraphQLWsLink(
    createClient({
      url: endpointWs,
      // Переподключение сокета: экраны с живым состоянием перезапрашивают
      // данные, события подписок за время обрыва потеряны.
      on: { connected: notifyWsConnected },
      connectionParams: () => {
        // Получаем токен из localStorage (сохраненный HTTP link)
        const tokenFromStorage = localStorage.getItem("auth-token");

        // Fallback: пробуем получить из cookies
        const tokenFromCookies = document.cookie
          .split("; ")
          .find((row) => row.startsWith("auth="))
          ?.split("=")[1];

        const token = tokenFromStorage || tokenFromCookies;

        // Дебаг информация
        console.log("WebSocket connectionParams debug:", {
          tokenFromStorage,
          tokenFromCookies,
          finalToken: token,
          hasToken: !!token,
        });

        const params = {
          // Передаем токен если есть
          authorization: token ? `Bearer ${token}` : undefined,
          // Добавляем заголовки для обхода CSRF защиты
          "Apollo-Require-Preflight": "true",
          "Accept-Language": "ru",
          "X-Referer": window.location.href,
        };

        console.log("WebSocket connectionParams:", params);
        return params;
      },
    })
  );

  let errorsCount = 0;

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    console.log({ graphQLErrors, networkError });

    errorsCount++;

    if (typeof window !== "undefined") {
      if (errorsCount > 100) {
        window.location.reload();
      }
    }
  });

  const auth = new ApolloLink((operation, forward) => {
    const url = document.location.href;

    const headers = {
      "Accept-Language": "ru",
      "X-Referer": url,
      "Apollo-Require-Preflight": "true",
    };

    operation.setContext({ headers });

    const result = forward(operation);

    if (!result.map) return result;

    return result.map((response) => {
      const context = operation.getContext();

      // Ловим токен из заголовка ответа и сохраняем в localStorage
      if (context.response && context.response.headers) {
        const authToken = context.response.headers.get("X-Auth-Token");
        if (authToken) {
          localStorage.setItem("auth-token", authToken);
        }
      }

      return response;
    });
  });

  // Разделение запросов: subscriptions идут через WebSocket, остальные через HTTP
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    from([errorLink, auth, uploadLink])
  );

  const cache = new InMemoryCache({ typePolicies });
  const originalDiff = cache.diff.bind(cache);
  // Avoid deprecated canonizeResults leaking into cache.diff options.
  cache.diff = ((options?: DiffOptions) => {
    const strippedOptions = stripCanonizeResults(options);
    if (!strippedOptions) {
      throw new Error("cache.diff requires options");
    }
    return originalDiff(strippedOptions);
  }) as typeof cache.diff;

  return new ApolloClient({
    link: splitLink,
    cache,
    // Опрос любого запроса (рассылка, автоматизация, участники) пропускает
    // тик, пока вкладка браузера скрыта: ночь с открытой вкладкой не
    // стоит серверу тысяч запросов (супер-ревью кабинета 4).
    defaultOptions: { watchQuery: { skipPollAttempt: skipHiddenPagePoll } },
  });
};

export { skipHiddenPagePoll };
